import { AWS_API_URL, DEVICE_NAME } from '@/constants'
import { getAuthStoreSnapshot } from '@/api/getAuthStoreSnapshot'
import { recordException } from '@/utils/sentry'
import { createAndReturnUniqueDeviceId } from '@/utils/uniqueDeviceId'

export interface PaginationParamsType {
  page: number
  limit: number
  total: number
  totalPages: number
  hasPrev: boolean
  hasNext: boolean
}

interface FilterParamsType {
  sortableBy: string[]
  filterableBy: string[]
  appliedSort: Record<string, 'ASC' | 'DESC'>
  appliedFilter: Record<string, string>
}

export type SortingDirType = FilterParamsType['appliedSort'][string]

export interface ComplexResponseType<T> {
  items: T
  pagination?: PaginationParamsType,
  filters?: FilterParamsType
}

export interface ErrorType {
  errors?: unknown[]
  items?: unknown[]
  properties?: unknown
}

export type ResponseType<T> =
  | {
    data: T
    message: string
    statusCode: number
    success: true
  }
  | {
    data: undefined
    error?: ErrorType
    message: string
    statusCode: number
    success: false
  }

export default async function request<T>({
  endpoint = '/',
  method = 'GET',
  body = undefined,
  multipart = false
}: {
  endpoint: string
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'
  headers?: HeadersInit
  body?: FormData | Record<string, unknown> | Record<string, unknown>[]
  multipart?: boolean
}): Promise<ResponseType<T>> {
  const requestHeaders: HeadersInit = new Headers()
  requestHeaders.set('Accept', 'application/json')
  if (!!body && !multipart) requestHeaders.set('Content-Type', 'application/json')

  const authStoreData = await getAuthStoreSnapshot()
  const tokenData = authStoreData.credentials
  if (tokenData) {
    requestHeaders.set(
      'Authorization',
      `${tokenData.token_type} ${tokenData.access_token}`
    )
  }

  // Current Organization ID Header
  const currentOrgId = authStoreData.currentOrganization?.id
  if (currentOrgId) {
    requestHeaders.set('x-acting-organization-id', currentOrgId)
  }

  // Device ID Header
  requestHeaders.set('x-device-id', createAndReturnUniqueDeviceId())

  // Device Info Header
  requestHeaders.set('x-device-info', DEVICE_NAME)

  // Device Timezone Header
  requestHeaders.set('x-device-timezone', Intl.DateTimeFormat().resolvedOptions().timeZone)

  const config: RequestInit = {
    method,
    headers: requestHeaders
  }

  if (body && method !== 'GET') {
    config.body = multipart && body instanceof FormData
      ? body
      : JSON.stringify(body)
  }

  const startTime = Date.now()
  const finalUrl = `${AWS_API_URL}/dashboard/v1${endpoint}`
  const response = await fetch(finalUrl, config)
  const elapsedTime = Date.now() - startTime
  // LOGGING
  const toLog = [
    '🌩️',
    method,
    response.status,
    finalUrl,
    elapsedTime / 1000 + 's'
  ]
  if (body) toLog.push('\n🌩️ BODY', JSON.stringify(body))
  // eslint-disable-next-line no-console
  console.log(...toLog)
  // STOP LOGGING

  if (response.ok) {
    return (await response.json()) as ResponseType<T>
  } else {
    const errorBody = (response.json ? await response.json() : {}) as ResponseType<T>
    if (response.status === 404) {
      return Promise.reject(errorBody)
    }

    const sentryError = new Error(
      `Server error ${response.status} on [${config.method}] ${endpoint}`,
      { cause: errorBody?.message || response.statusText }
    )

    if (response.status >= 500)
      sentryError.message += `\n\n${errorBody?.message || 'Internal Server Error'}`

    recordException(sentryError, {
      tags: {
        endpoint,
        method: config.method
      }
    })

    return Promise.reject(errorBody)
  }

}

export class ApiFileError extends Error {
  status: number
  body?: unknown
  constructor(message: string, status: number, body?: unknown) {
    super(message)
    this.name = 'ApiFileError'
    this.status = status
    this.body = body
  }
}

interface RequestFileResult {
  blob: Blob
  filename?: string
  contentType?: string | null
}

function getFilenameFromContentDisposition(contentDisposition: string | null): string | undefined {
  if (!contentDisposition) return undefined

  // RFC 5987 / 6266: filename*=UTF-8''... takes precedence over filename=
  const filenameStar = contentDisposition.match(/filename\*\s*=\s*([^;]+)/i)?.[1]?.trim()
  if (filenameStar) {
    const value = filenameStar.replace(/^UTF-8''/i, '').replace(/^"(.*)"$/, '$1')
    try {
      return decodeURIComponent(value)
    } catch {
      return value
    }
  }

  const filename = contentDisposition.match(/filename\s*=\s*([^;]+)/i)?.[1]?.trim()
  if (!filename) return undefined
  return filename.replace(/^"(.*)"$/, '$1')
}

export async function requestFile({
  endpoint = '/',
  method = 'GET',
  timeoutMs = 120_000
}: {
  endpoint: string
  method?: 'GET'
  timeoutMs?: number
}): Promise<RequestFileResult> {
  const requestHeaders: HeadersInit = new Headers()
  requestHeaders.set(
    'Accept',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel, */*'
  )

  const authStoreData = await getAuthStoreSnapshot()
  const tokenData = authStoreData.credentials
  if (tokenData) {
    requestHeaders.set(
      'Authorization',
      `${tokenData.token_type} ${tokenData.access_token}`
    )
  }

  const currentOrgId = authStoreData.currentOrganization?.id
  if (currentOrgId) {
    requestHeaders.set('x-acting-organization-id', currentOrgId)
  }

  requestHeaders.set('x-device-id', createAndReturnUniqueDeviceId())
  requestHeaders.set('x-device-info', DEVICE_NAME)
  requestHeaders.set('x-device-timezone', Intl.DateTimeFormat().resolvedOptions().timeZone)

  const finalUrl = `${AWS_API_URL}/dashboard/v1${endpoint}`

  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), timeoutMs)
  try {
    const response = await fetch(finalUrl, { method, headers: requestHeaders, signal: controller.signal })

    if (!response.ok) {
      const errorBody = response.headers.get('content-type')?.includes('application/json')
        ? await response.json().catch(() => ({}))
        : {}
      const message = (errorBody as { message?: string })?.message || response.statusText
      return Promise.reject(new ApiFileError(message, response.status, errorBody))
    }

    const contentDisposition = response.headers.get('content-disposition')
    const filename = getFilenameFromContentDisposition(contentDisposition)
    return {
      blob: await response.blob(),
      filename,
      contentType: response.headers.get('content-type')
    }
  } finally {
    clearTimeout(timeout)
  }
}

